import { onMounted, ref, computed, watch } from 'vue';
import * as baseConfig  from "../../src/js/baseConfig";
import type { Ref } from 'vue';
import apifetch from '../../../vueforms/src/utils/apifetch';

/**
 * Zugriff auf das Gewässernetz und die Längsschnitt-Daten
 */

export interface SeriesDescriptor {
  typ: string,
  gewaesser: string, // Gewässernummer
  file: string,
  source: string,
  length: number
};

/**
 * nr: Die Gewässernummer
 * name: Ein Array von den Gewässernamen dieses Gewässers
 * types: Ein Objekt, das für die Längsschnitt-Typen (MQ, HQxy, Max. SE-Abfluss) Arrays mit Daten in der Form {x: 1, y: 2} enthält
 */
export class Gewaesser {
  nr: string;
  name: Array<string>;
  laengsschnitte: Ref<SeriesDescriptor[]>;
  constructor(nr: string) {
    this.nr = nr;
    this.name = [];
    this.laengsschnitte = ref([]);
  }
}

const seriesFilePromise = (seriesFile: string) => apifetch(baseConfig.getBaseURL() + "modeldata/laengsschnitt/" + seriesFile).then(res => res.json());

/* Liefert ein Promise zurück: Ein Array von Längsschnitt-Daten.
   Die Daten sind in der Form {x: 1, y: 2}
   Das Array ist synchron zum  gewaesser.laengsschnitte-Array sortiert 
*/
const getGewaesserSeries = (gewaesser: Gewaesser) => {
  const seriesFilePromises: Array<Promise<Array<any>>> = gewaesser.laengsschnitte.value.map( ls=>seriesFilePromise(ls.file) );
  return Promise.all(seriesFilePromises);
}

const allSeries: Ref<SeriesDescriptor[]> = ref([])
apifetch(baseConfig.getBaseURL() + "modeldata/laengsschnitt/series.json").
         then(res => res.json()).
         then(j=> allSeries.value.push(... j ))


const seArray: Ref<Object[]> = ref([])
apifetch(baseConfig.getBaseURL() + "modeldata/se.json").
         then(res => res.json()).
         then(j=> seArray.value.push(... j ))

const gewaesserGeom: Ref<{ [key: string]: any }> = ref({});

const getGewaesserGeom = async () => {
  try {
    const res = await apifetch(baseConfig.getBaseURL() + "modeldata/GewaesserGeom.json", null, true);
    const j = await res.json();
    gewaesserGeom.value = j;
  } catch (error) {
    console.warn('Die GewaesserGeom.json Datei wurde nicht gefunden:', error);
    return {};
  }
}
getGewaesserGeom();

// Alle Abfluss-Längsschnitt Serien
const qSeries = computed<SeriesDescriptor[]>(() =>
  allSeries.value.filter((series: SeriesDescriptor) => series.source === "Abfluss" && series.length>1)
);

/*
 * Die Map, die die Gewässer mit ihren Längsschnitt-Daten enthält
 * key: Gewässernummer
 * value: gewaesser-Objekt
*/
const gewaesserMap = computed<{ [key: string]: Gewaesser }>(() => {
  let result : { [key: string]: Gewaesser } = {};
  for (let i = 0; i < qSeries.value.length; i++) {
    const seriesIter : SeriesDescriptor = qSeries.value[i];
    const gewaesserNr = seriesIter.gewaesser;
    if (!result[gewaesserNr]) {
      result[gewaesserNr] = new Gewaesser(gewaesserNr);
    }
    let gew = result[gewaesserNr];
    // Übernehme den Gewässernamen von ID_GewaesserShape
    if (gewaesserGeom.value[gewaesserNr]?.name) {
      if (!gew.name.includes(gewaesserGeom.value[gewaesserNr].name)) {
        gew.name.push(gewaesserGeom.value[gewaesserNr].name);
      }
    } 
    // Für den Fall, dass kein Name eingetragen ist, übernehme alle Gewässernamen aus den TE für diese Gewässernummer
    else {
      for (let se of seArray.value) {
        if (se.ID_GewaessernummerAb === seriesIter.gewaesser) {
          //Dem gewaesser Eintrag die möglichen Gewässernamen aus Systemelements-Info zuordnen
          let name = se.ID_Transportelemente?.ID_TE_Bezeichnung;
          if (name && !gew.name.includes(name)) {
            gew.name.push(name);
          }
        }
      }
    }
    gew.laengsschnitte.value.push(seriesIter);
  }
  return result;
});

/**
 * gewaesserAuswahl ist ein computed Array, der alle relevanten Gewässernummern enthält, ohne Duplikate
 */
const gewaesserAuswahl = computed<{ Array<Gewaesser> }> (() => {
  let uniqueSeries : Array<string> = [];
  qSeries.value.forEach((s : SeriesDescriptor) => {
      if (!uniqueSeries.includes(s.gewaesser) && gewaesserMap.value[s.gewaesser]) {
        uniqueSeries.push(s.gewaesser);
      }
  });
  uniqueSeries.sort()
  return uniqueSeries.map( (nr) => gewaesserMap.value[nr]);
})

/**
 * Nummer des Gewässers mit den meisten Längsschnitt-Daten
 */
const laengstesGewaesser = computed<string|null>( () => {
  let result = null;
  let maxLength = 0;
  for (let i = 0; i < qSeries.value.length; i++) {
    let aSeries = qSeries.value[i];
    if (aSeries.length > maxLength) {
      maxLength = aSeries.length;
      result = aSeries.gewaesser;
    }
  }
  return result;
});


const gewaessernetz = {
  // Map: Gewässernummer -> Gewässer
  gewaesserMap,
  // Array von Gewässern
  gewaesserAuswahl,
  // Nummer des Gewässers mit den meisten Längsschnitt-Daten
  laengstesGewaesser,
  // Liefert zu einem Gewässer ein Promise mit den Längsschnitt-Daten
  getGewaesserSeries
};

export default gewaessernetz;